import { useContext, useRef, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/core';
import { useAppMetadataContext } from '../../components/AppMetadataContextWrapper';
import convertRequestStateToSearchParams from '../../services/convertRequestStateToSearchParams';
import useMessageService from '../../services/i18n/useMessageService';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import PageContainer from '../../components/PageContainer';
import SelfRegistrationForm from './SelfRegistrationForm';
import { isValidEmail } from '../../services/emailUtils';
import ResponseStatus from '../../constants/ResponseStatus';
import { requestNonVaultAppRegistration, requestSelfRegistration } from '../../veevaIdApi';
import SelfRegistrationConfirmation from './SelfRegistrationConfirmation';
import RecaptchaV2Invisible from '../../components/RecaptchaV2Invisible';
import { getFirstError, showErrorNotificationFromResponse } from '../../services/errorUtils';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import useRequestState from '../../services/useRequestState';
import RegistrationTypes from '../../constants/RegistrationTypes';
import { UnauthenticatedPaths } from '../../constants/Paths';
import getTargetAttribute from '../../services/getTargetAttribute';
import hasValidASCIICharacters from '../../services/hasValidASCIICharacters';
import InvalidUsernameCharactersInlineMessage from '../../components/InvalidUsernameCharactersInlineMessage';

const SelfRegistrationPage = () => {
    const [
        [
            emailSentTitleMessage = 'If you are a new user, an email has been sent to:',
            startRegistrationTitle = 'Start Your VeevaID Registration',
            invalidEmailError = 'Invalid email format entered',
            recaptchaError = 'Recaptcha verification failed. Please try again.',
            nonVaultAppPageTitle = 'VeevaID is required for {0}',
            nonVaultAppEmailSentTitleMessage = 'An email has been sent to:',
            vaultIdAlreadyInUseMessage = 'Username already in use by another Vault user.',
            createAliasEmail = 'Create {0} (alias).',
            buttonLabel = 'Continue',
        ],
    ] = useMessageService([
        'register.self_register_email_sent_title',
        'register.welcome_self_registration',
        'general.invalid_email',
        'error.veevaid_invalid_recaptcha',
        'register.non_vault_app_page_title',
        'register.self_register_non_vault_app_email_sent_title',
        'register.vault_id_already_in_use',
        'register.create_alias_email',
        'general.continue',
    ]);
    const { appMetadata, isNonVaultApp } = useAppMetadataContext();
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const { setLoading } = useContext(LoadingScreenContext);
    const [email, setEmail] = useState('');
    const [plusOneEmail, setPlusOneEmail] = useState();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [error, setError] = useState('');
    const { showNotification } = useNetworkErrorNotification();
    const resetCaptcha = useRef();
    const executeCaptcha = useRef();
    const history = useHistory();
    const requestState = useRequestState();
    const requestStateParams = convertRequestStateToSearchParams(requestState);

    const invalidUsernameCharsError = useMemo(
        () => <InvalidUsernameCharactersInlineMessage show />,
        [],
    );

    useEffect(() => {
        setError('');
    }, [messageLanguage]);

    const setExecuteRef = (callback) => {
        executeCaptcha.current = callback;
    };

    const setResetRef = (callback) => {
        resetCaptcha.current = callback;
    };

    const handleEmailChange = (value) => {
        setEmail(value);
        setError(''); // clear error as user types new email
    };

    const handleSubmit = async () => {
        if (!isValidEmail(email)) {
            setError(invalidEmailError);
            return;
        }

        if (!hasValidASCIICharacters(email)) {
            setError(invalidUsernameCharsError);
            return;
        }

        setError('');
        const token = await executeCaptcha.current?.();
        setLoading(true);

        let response;
        const username = plusOneEmail ?? email;
        if (appMetadata?.registration_type === RegistrationTypes.NON_VAULT_APP) {
            response = await requestNonVaultAppRegistration({
                email: username,
                recaptchaToken: token,
                requestState,
            });
        } else {
            response = await requestSelfRegistration({
                email: username,
                recaptchaToken: token,
                requestState,
            });
        }

        setLoading(false);
        resetCaptcha.current?.();
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                if (response?.data?.targetUrl) {
                    const newUrl = new URL(response.data.targetUrl);
                    const currentUrl = window.location;
                    const originsMatch = newUrl.origin === currentUrl.origin;
                    const pathnameIsLogin =
                        newUrl.pathname === UnauthenticatedPaths.LOGIN ||
                        newUrl.pathname === '/login';

                    if (originsMatch && pathnameIsLogin) {
                        // pass username only when origins match and path is login
                        history.push({
                            pathname: newUrl.pathname,
                            search: newUrl.search,
                            state: {
                                username: email,
                            },
                        });
                    } else {
                        window.location.href = response.data.targetUrl;
                    }
                } else if (response.data?.email) {
                    const plusOneErrorMessage = `${vaultIdAlreadyInUseMessage} ${createAliasEmail?.replace(
                        '{0}',
                        response.data?.email,
                    )}`;
                    setPlusOneEmail(response.data?.email);
                    setError(plusOneErrorMessage);
                    setShowConfirmation(false);
                } else {
                    setShowConfirmation(true);
                }
                break;
            case ResponseStatus.FAILURE: {
                const responseError = getFirstError(response);
                switch (responseError.type) {
                    case ResponseErrorTypes.VEEVAID_INVALID_RECAPTCHA: {
                        setError(recaptchaError);
                        break;
                    }
                    case ResponseErrorTypes.VEEVAID_INVALID_DATA: {
                        setError(invalidEmailError);
                        break;
                    }
                    default: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNotification();
                break;
            default:
            // No-op
        }
    };

    const getPageTitle = () => {
        if (showConfirmation) {
            return (
                <span
                    data-target={getTargetAttribute('self-register-confirmation-title')}
                    css={css`
                        font-weight: normal;
                    `}
                >
                    {`${isNonVaultApp ? nonVaultAppEmailSentTitleMessage : emailSentTitleMessage} `}
                    <strong>{plusOneEmail ?? email}</strong>
                </span>
            );
        }
        if (isNonVaultApp) {
            return nonVaultAppPageTitle?.replace('{0}', appMetadata.app_name);
        }
        return startRegistrationTitle;
    };

    return (
        <PageContainer title={getPageTitle()}>
            <RecaptchaV2Invisible reset={setResetRef} execute={setExecuteRef} />
            {showConfirmation ? (
                <SelfRegistrationConfirmation />
            ) : (
                <SelfRegistrationForm
                    buttonLabel={buttonLabel}
                    email={email}
                    error={error}
                    onChange={handleEmailChange}
                    onSubmit={handleSubmit}
                    requestStateParams={requestStateParams}
                />
            )}
        </PageContainer>
    );
};

export default SelfRegistrationPage;
